import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideDownload,
  lucideLayoutTemplate,
  lucidePencil,
  lucideRepeat,
  lucideTriangleAlert,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { CompanyService } from '../../../../../services/company.service';
import {
  VoucherDesign,
  VoucherDesignService,
  VoucherType,
  ptToMm,
  voucherTypeLabel,
} from '../../../../../services/voucher-design.service';
import { DesignThumbnailComponent } from '../design-thumbnail/design-thumbnail.component';

export interface Slot {
  type: VoucherType;
  label: string;
  design: VoucherDesign | null;
  /** The product slot only matters while product vouchers are on. */
  enabled: boolean;
}

/** The two slots: which design money vouchers and product vouchers print on. */
@Component({
  selector: 'app-active-designs',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    DesignThumbnailComponent,
  ],
  providers: [
    provideIcons({
      lucideDownload,
      lucideLayoutTemplate,
      lucidePencil,
      lucideRepeat,
      lucideTriangleAlert,
    }),
  ],
  templateUrl: './active-designs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveDesignsComponent {
  private readonly designService = inject(VoucherDesignService);
  private readonly companyService = inject(CompanyService);

  readonly designs = input<VoucherDesign[]>([]);
  readonly loading = input(false);
  /** The design a PDF preview is being made for. */
  readonly previewingId = input<string | null>(null);

  readonly switchSlot = output<VoucherType>();
  readonly preview = output<VoucherDesign>();

  readonly slots = computed<Slot[]>(() => {
    const company = this.companyService.company();
    const designs = this.designs();
    return (['money', 'product'] as VoucherType[]).map((type) => ({
      type,
      label: voucherTypeLabel(type),
      design: this.designService.activeDesign(designs, type),
      enabled: type === 'money' || !!company?.voucher_products_enabled,
    }));
  });

  sizeLabel(design: VoucherDesign): string {
    return `${ptToMm(design.page_width_pt)} × ${ptToMm(design.page_height_pt)} mm`;
  }
}
